import { useState } from "react";
import useSWR from "swr";
import { Pencil, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg, fetcher, imgUrl, GENDERS } from "../../lib/api";
import { PageHeader, GoldButton } from "../../components/admin/AdminUI";
import { Switch } from "../../components/ui/switch";
import { Input } from "../../components/ui/input";
import ConfirmDelete from "../../components/admin/ConfirmDelete";
import ProductForm from "./ProductForm";

export default function Products() {
  const { data, mutate } = useSWR("/admin/products", fetcher);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const patch = async (p, changes) => {
    try {
      await api.patch(`/admin/products/${p.id}`, changes);
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/admin/products/${toDelete.id}`);
      toast.success("Produto excluído");
      mutate();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  const items = (data || []).filter((p) => `${p.name} ${p.brand}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-products">
      <PageHeader title="Produtos" subtitle={`${data?.length ?? 0} perfumes cadastrados`}>
        <GoldButton onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-product-button"><Plus className="h-4 w-4" />Novo produto</GoldButton>
      </PageHeader>
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A1A1AA]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou marca" className="pl-9" data-testid="admin-products-search" />
      </div>
      {data && items.length === 0 && <p className="rounded-2xl border border-dashed border-white/10 p-12 text-center text-sm text-[#A1A1AA]" data-testid="admin-products-empty">Nenhum produto cadastrado ainda. Clique em “Novo produto” para começar.</p>}
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/5 bg-[#141417] p-4" data-testid={`admin-product-row-${p.id}`}>
            <div className="h-16 w-14 shrink-0 overflow-hidden rounded-lg bg-black">{p.images?.[0] && <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-cover" />}</div>
            <div className="min-w-[180px] flex-1">
              <p className="font-serif text-xl">{p.name}</p>
              <p className="text-xs text-[#A1A1AA]">{p.brand} · {p.volume_ml}ml · {GENDERS[p.gender]}</p>
            </div>
            <div className="w-28 text-sm">
              <p className="text-[#F3E5AB]">{brl(p.promo_price || p.price)}</p>
              {p.promo_price && <p className="text-xs text-[#A1A1AA] line-through">{brl(p.price)}</p>}
            </div>
            <p className={`w-24 text-xs ${p.stock > 0 ? "text-emerald-400" : "text-red-400"}`}>{p.stock} em estoque</p>
            <label className="flex items-center gap-2 text-xs text-[#A1A1AA]"><Switch checked={p.active} onCheckedChange={(v) => patch(p, { active: v })} data-testid={`admin-product-active-${p.id}`} />Ativo</label>
            <label className="flex items-center gap-2 text-xs text-[#A1A1AA]"><Switch checked={p.featured} onCheckedChange={(v) => patch(p, { featured: v })} data-testid={`admin-product-featured-${p.id}`} />Destaque</label>
            <div className="flex gap-1">
              <button onClick={() => { setEditing(p); setOpen(true); }} className="rounded-full p-2 text-[#A1A1AA] hover:bg-white/5 hover:text-[#F3E5AB]" aria-label="Editar" data-testid={`admin-product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => setToDelete(p)} className="rounded-full p-2 text-[#A1A1AA] hover:bg-white/5 hover:text-red-400" aria-label="Excluir" data-testid={`admin-product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <ProductForm open={open} onOpenChange={setOpen} product={editing} onSaved={mutate} />
      <ConfirmDelete open={!!toDelete} onCancel={() => setToDelete(null)} onConfirm={remove} title="Excluir produto?" description={`“${toDelete?.name}” será removido do site e das promoções.`} />
    </div>
  );
}
